/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { filterDOMProps } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import { type ContextValue, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface DividerProps extends Omit<HTMLAttributes<HTMLHRElement>, 'style' | 'className' | 'children'> {
  slot?: string;
  /** Whether the divider is dashed. (Default: false) */
  isDashed?: boolean;
  /** Whether the divider has margin. (Default: false) */
  withMargin?: boolean;
  /** The size of the divider. (Default: 1) */
  size?: keyof typeof sizeVariants;
  /** The variant of the divider. (Default: medium) */
  variant?: keyof typeof variantVariants;
  style?: stylex.StyleXStyles;
}

const DividerContext = createContext<ContextValue<DividerProps, HTMLHRElement>>({});

const Divider = forwardRef((props: DividerProps, ref: ForwardedRef<HTMLHRElement>) => {
  [props, ref] = useContextProps(props, ref, DividerContext);

  const {
    size = 1,
    variant = 'medium',
    isDashed = false,
    withMargin = false,
    style,
    ...otherProps
  } = props;

  return (
    <hr
      {...filterDOMProps(otherProps)}
      role="presentation"
      ref={ref}
      {...stylex.props(
        styles.base,
        withMargin && styles.withMargin,
        sizeVariants[size],
        isDashed ? dashedVariantVariants[variant] : variantVariants[variant],
        style
      )}
    />
  );
});

const styles = stylex.create({
  base: {
    margin: 0,
    padding: 0,
    borderStyle: 'none',
    width: '100%',
  },
  withMargin: {
    margin: `0px 8px`,
    width: `calc(100% - 8px * 2)`,
  },
});

const variantVariants = stylex.create({
  silent: { backgroundColor: $.opacity06 },
  medium: { backgroundColor: $.opacity08 },
  loud: { backgroundColor: $.opacity16 },
  extraLoud: { backgroundColor: $.borderContrast },
  inverted: { backgroundColor: $.borderContrastInverted },
  interactive: { backgroundColor: $.borderInteractive },
});

const dashedVariantVariants = stylex.create({
  silent: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.opacity06} 0px, ${$.opacity06} 4px, transparent 4px, transparent 10px)`
  },
  medium: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.opacity08} 0px, ${$.opacity08} 4px, transparent 4px, transparent 10px)`
  },
  loud: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.opacity16} 0px, ${$.opacity16} 4px, transparent 4px, transparent 10px)`
  },
  extraLoud: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.borderContrast} 0px, ${$.borderContrast} 4px, transparent 4px, transparent 10px)`
  },
  inverted: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.borderContrastInverted} 0px, ${$.borderContrastInverted} 4px, transparent 4px, transparent 10px)`
  },
  interactive: {
    backgroundColor: `repeating-linear-gradient(90deg, ${$.borderInteractive} 0px, ${$.borderInteractive} 4px, transparent 4px, transparent 10px)`
  },
});

const sizeVariants = stylex.create({
  0.5: {
    height: 0.5,
  },
  1: {
    height: 1,
  },
  2: {
    height: 2,
  },
});

export { Divider, DividerContext, type DividerProps };
